import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import usePreventScroll from '../hooks/usePreventScroll';
import api from '../services/api';

const UserProfile = ({ isOpen, onClose }) => {
  const { user, logout, setUser } = useAuth();
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber || '');
  const [phoneError, setPhoneError] = useState('');
  const [phoneSuccess, setPhoneSuccess] = useState('');
  const [isUpdatingPhone, setIsUpdatingPhone] = useState(false);
  const [userStats, setUserStats] = useState(null);

  // Prevent background scrolling when modal is open
  usePreventScroll(isOpen);

  // Fetch user stats for gamification display
  useEffect(() => {
    const fetchUserStats = async () => {
      if (isOpen && user) {
        try {
          const response = await api.get('/api/user-dashboard/stats');
          setUserStats(response.data);
        } catch (error) {
          console.error('Failed to fetch user stats:', error);
        }
      }
    };
    fetchUserStats();
  }, [isOpen, user]);

  if (!isOpen) return null;

  const handleLogout = () => {
    logout();
    onClose();
  };

  const handlePhoneUpdate = async () => {
    setIsUpdatingPhone(true);
    setPhoneError('');
    setPhoneSuccess('');

    try {
      const response = await api.post('/api/auth/update-phone', {
        phoneNumber: phoneNumber || null
      });

      if (response.data.requiresVerification) {
        setPhoneSuccess('Phone number updated! Please check your phone for verification code.');
        // Refresh user data
        const meResponse = await api.get('/api/auth/me');
        setUser(meResponse.data);
      } else {
        setPhoneSuccess('Phone number removed successfully.');
        // Refresh user data
        const meResponse = await api.get('/api/auth/me');
        setUser(meResponse.data);
      }
      
      setIsEditingPhone(false);
    } catch (error) {
      setPhoneError(error.response?.data?.error || 'Failed to update phone number');
    }

    setIsUpdatingPhone(false);
  };

  const handlePhoneCancel = () => {
    setPhoneNumber(user?.phoneNumber || '');
    setIsEditingPhone(false);
    setPhoneError('');
    setPhoneSuccess('');
  };

  const getInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    }
    if (user?.firstName) {
      return user.firstName.charAt(0).toUpperCase();
    }
    if (user?.username) {
      return user.username.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const getFullName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.firstName) {
      return user.firstName;
    }
    return user?.username || 'User';
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div 
        className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with Gradient Background */}
        <div className="relative bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 px-8 py-12 rounded-t-3xl">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-200 hover:rotate-90"
          >
            <span className="text-2xl leading-none">×</span>
          </button>

          {/* Profile Avatar and Name */}
          <div className="flex flex-col items-center text-center">
            <div className="relative">
              <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center shadow-2xl mb-4">
                <span className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  {getInitials()}
                </span>
              </div>
              {user?.role === 'ADMIN' && (
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                  ADMIN
                </div>
              )}
            </div>
            
            <h2 className="text-3xl font-bold text-white mb-2">
              {getFullName()}
            </h2>
            <p className="text-white/90 text-lg mb-1">@{user?.username}</p>
            <p className="text-white/80 text-sm">{user?.email}</p>
          </div>

          {/* Stats Cards */}
          {userStats && (
            <div className="grid grid-cols-3 gap-4 mt-8">
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 text-center border border-white/20">
                <div className="text-3xl font-bold text-white">{userStats.level || 1}</div>
                <div className="text-white/80 text-sm font-medium mt-1">Level</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 text-center border border-white/20">
                <div className="text-3xl font-bold text-white">{userStats.problemsSolved || 0}</div>
                <div className="text-white/80 text-sm font-medium mt-1">Problems Solved</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 text-center border border-white/20">
                <div className="text-3xl font-bold text-white">{userStats.currentStreak || 0}</div>
                <div className="text-white/80 text-sm font-medium mt-1">Day Streak</div>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Account Information Section */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="w-1 h-6 bg-gradient-to-b from-blue-600 to-purple-600 rounded-full mr-3"></span>
              Account Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* First Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  First Name
                </label>
                <div className="px-4 py-3.5 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-xl text-gray-900 font-medium">
                  {user?.firstName || 'Not provided'}
                </div>
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Last Name
                </label>
                <div className="px-4 py-3.5 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-xl text-gray-900 font-medium">
                  {user?.lastName || 'Not provided'}
                </div>
              </div>

              {/* Username */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Username
                </label>
                <div className="px-4 py-3.5 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-xl text-gray-900 font-medium">
                  @{user?.username}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="px-4 py-3.5 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-xl flex items-center justify-between">
                  <span className="text-gray-900 font-medium">{user?.email}</span>
                  {user?.isEmailVerified ? (
                    <span className="flex items-center text-green-600 text-sm font-semibold bg-green-50 px-3 py-1 rounded-full">
                      <span className="mr-1">✓</span> Verified
                    </span>
                  ) : (
                    <span className="flex items-center text-amber-600 text-sm font-semibold bg-amber-50 px-3 py-1 rounded-full">
                      <span className="mr-1">⏳</span> Pending
                    </span>
                  )}
                </div>
              </div>
              {/* Phone Number */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number
                </label>
                {isEditingPhone ? (
                  <div className="space-y-3">
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="Enter phone number (e.g., +1234567890)"
                      className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                    <div className="flex space-x-3">
                      <button
                        onClick={handlePhoneUpdate}
                        disabled={isUpdatingPhone}
                        className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                      >
                        {isUpdatingPhone ? 'Updating...' : 'Save Changes'}
                      </button>
                      <button
                        onClick={handlePhoneCancel}
                        disabled={isUpdatingPhone}
                        className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                    {phoneError && (
                      <div className="flex items-center text-red-600 text-sm bg-red-50 px-4 py-3 rounded-xl border border-red-200">
                        <span className="mr-2">⚠️</span> {phoneError}
                      </div>
                    )}
                    {phoneSuccess && (
                      <div className="flex items-center text-green-600 text-sm bg-green-50 px-4 py-3 rounded-xl border border-green-200">
                        <span className="mr-2">✓</span> {phoneSuccess}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="px-4 py-3.5 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-xl flex items-center justify-between">
                    <span className="text-gray-900 font-medium">{user?.phoneNumber || 'Not provided'}</span>
                    <div className="flex items-center space-x-3">
                      {user?.phoneNumber && (
                        user?.isPhoneVerified ? (
                          <span className="flex items-center text-green-600 text-sm font-semibold bg-green-50 px-3 py-1 rounded-full">
                            <span className="mr-1">✓</span> Verified
                          </span>
                        ) : (
                          <span className="flex items-center text-amber-600 text-sm font-semibold bg-amber-50 px-3 py-1 rounded-full">
                            <span className="mr-1">⏳</span> Pending
                          </span>
                        )
                      )}
                      <button
                        onClick={() => setIsEditingPhone(true)}
                        className="text-blue-600 hover:text-blue-700 text-sm font-semibold transition-colors"
                      >
                        {user?.phoneNumber ? '✏️ Edit' : '➕ Add Phone'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Account Role
                </label>
                <div className="px-4 py-3.5 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-xl">
                  <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-sm font-bold ${
                    user?.role === 'ADMIN' 
                      ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-md' 
                      : 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-md'
                  }`}>
                    {user?.role === 'ADMIN' ? '👑 Admin' : '👤 User'}
                  </span>
                </div>
              </div>

              {/* Auth Provider */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Login Method
                </label>
                <div className="px-4 py-3.5 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-xl">
                  <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-sm font-bold ${
                    user?.authProvider === 'GOOGLE' 
                      ? 'bg-blue-100 text-blue-800' 
                      : user?.authProvider === 'PHONE'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {user?.authProvider === 'GOOGLE' && '🔵 Google'}
                    {user?.authProvider === 'PHONE' && '📱 Phone'}
                    {user?.authProvider === 'LOCAL' && '📧 Email/Password'}
                    {!['GOOGLE', 'PHONE', 'LOCAL'].includes(user?.authProvider) && user?.authProvider}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 pt-6 border-t-2 border-gray-200 flex justify-between items-center">
            <button
              onClick={onClose}
              className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 hover:border-gray-400 transition-all"
            >
              Close
            </button>
            <button
              onClick={handleLogout}
              className="px-8 py-3 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-xl font-semibold hover:from-red-600 hover:to-pink-600 transition-all shadow-md hover:shadow-lg"
            >
              🚪 Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;

